import { matchCommands, type SlashCommand } from '@datum/shared'

interface SlashMenuProps {
  query: string
  activeIndex: number
  onPick: (command: SlashCommand) => void
  onHover: (index: number) => void
}

/** Presentational list; the editor owns arrow/enter/escape while it is open. */
export function SlashMenu({ query, activeIndex, onPick, onHover }: SlashMenuProps) {
  const commands = matchCommands(query)
  if (commands.length === 0) return null

  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      className="absolute bottom-full left-3 z-20 mb-2 w-80 overflow-hidden rounded-lg border border-edge-strong bg-ink-850 shadow-xl shadow-black/50"
    >
      <div className="border-b border-edge px-3 py-1.5">
        <span className="mono text-[10px] uppercase tracking-[0.12em] text-zinc-500">
          command /{query}
        </span>
      </div>
      {commands.map((command, index) => {
        const selected = index === ((activeIndex % commands.length) + commands.length) % commands.length
        return (
          <button
            key={command.id}
            type="button"
            role="option"
            aria-selected={selected}
            onMouseEnter={() => onHover(index)}
            onClick={() => onPick(command)}
            className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-[13px] ${
              selected ? 'bg-ink-700 text-zinc-50' : 'text-zinc-300 hover:bg-ink-800'
            }`}
          >
            <span className="flex items-baseline gap-2 truncate">
              <span className="mono text-sync">/{command.id}</span>
              <span className="truncate text-zinc-400">{command.description}</span>
            </span>
            <span className="mono shrink-0 text-[10px] text-zinc-600">{command.kind === 'action' ? 'upload' : 'block'}</span>
          </button>
        )
      })}
      <div className="border-t border-edge px-3 py-1.5">
        <span className="mono text-[10px] text-zinc-600">↑↓ select · enter apply · esc dismiss</span>
      </div>
    </div>
  )
}
